import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { fetchMedias } from "../../features/admin/MediaSlice";
import { toast } from "react-toastify";
import ApiCustom from "../../utils/ApiCustom";
import { formatDate } from "../../utils/helps";
import UserSelection from "../UserSelection";
import {
  PlusIcon,
  TrashIcon,
  PhotoIcon,
  PencilSquareIcon,
  ArrowsRightLeftIcon,
} from "@heroicons/react/24/outline";
import ConfirmDeleteCompanyUserModal from "../../components/ConfirmDeleteCompanyUserModal";
import ConfirmMakeCompanyUserRepresentModal from "../../components/ConfirmMakeCompanyUserRepresentModal";
import AvatarImageModal from "./AvatarImageModal";
import {
  LockClosedIcon,
  LockOpenIcon,
  NewspaperIcon,
} from "@heroicons/react/24/solid";
import apiCustom from "../../utils/ApiCustom";
import DatePicker from "../DatePicker";

const isApproved = localStorage.getItem("isApproved") === "true";

const companyPartnerSchema = Yup.object().shape({
  memberType: Yup.string().oneOf(["company", "individual"]).required(),
  companyName: Yup.string().trim().required("Tên đơn vị không được để trống"),
  userEmail: Yup.string()
    .trim()
    .email("Email đơn vị không đúng định dạng")
    .required("Email đơn vị không được để trống"),
  userPhone: Yup.string().trim().required("Số điện thoại không được để trống"),
  companyZaloPhone: Yup.string()
    .trim()
    .nullable()
    .matches(/^\d*$/, "Số điện thoại Zalo không hợp lệ"),
  businessField: Yup.string()
    .trim()
    .required("Ngành nghề kinh doanh không được để trống"),
  businessAddress: Yup.string()
    .trim()
    .required("Địa chỉ đơn vị không được để trống"),
  websiteOrHotline: Yup.string().nullable(),
  hotline: Yup.string().nullable().matches(/^\d*$/, "Hotline không hợp lệ"),
  taxCode: Yup.string().required("Mã số thuế không được để trống"),
  companyNameEn: Yup.string().nullable(),
  companyShortName: Yup.string().nullable(),
  ceoFullName: Yup.string().trim().required("Họ tên không được để trống"),
  ceoDateOfBirth: Yup.date()
    .typeError("Sinh nhật không hợp lệ")
    .required("Sinh nhật không được để trống"),
  ceoPhone: Yup.string()
    .trim()
    .required("Số điện thoại liên hệ không được để trống"),
  ceoEmail: Yup.string()
    .trim()
    .email("Email không đúng định dạng")
    .required("Email không được để trống"),
  charterCapital: Yup.string().trim().nullable(),
  employeeCount: Yup.string().trim().nullable(),
  establishedDate: Yup.date()
    .typeError("Ngày thành lập không hợp lệ")
    .required("Ngày thành lập không được để trống"),
  productService: Yup.string().trim().nullable(),
  branchOffice: Yup.string().nullable(),
  position: Yup.string().trim().nullable(),
  joinAt: Yup.date()
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue == null) return null;
      return value;
    })
    .typeError("Ngày tham gia hội không hợp lệ")
    .required("Ngày tham gia hội không được để trống"),
});

const individualPartnerSchema = Yup.object().shape({
  memberType: Yup.string().oneOf(["company", "individual"]).required(),
  companyName: Yup.string().trim().required("Tên hội viên không được để trống"),
  userEmail: Yup.string()
    .trim()
    .email("Email hội viên không đúng định dạng")
    .nullable(),
  userPhone: Yup.string()
    .trim()
    .required("Số điện thoại liên hệ không được để trống"),
  companyZaloPhone: Yup.string()
    .trim()
    .nullable()
    .matches(/^\d*$/, "Số điện thoại Zalo không hợp lệ"),
  businessField: Yup.string()
    .trim()
    .required("Ngành nghề kinh doanh không được để trống"),
  businessAddress: Yup.string()
    .trim()
    .required("Địa chỉ hội viên không được để trống"),
  websiteOrHotline: Yup.string().nullable(),
  hotline: Yup.string().nullable().matches(/^\d*$/, "Hotline không hợp lệ"),
  taxCode: Yup.string().nullable(),
  companyNameEn: Yup.string().nullable(),
  companyShortName: Yup.string().nullable(),
  ceoFullName: Yup.string().nullable(),
  ceoDateOfBirth: Yup.date()
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue == null) return null;
      return value;
    }),
  ceoPhone: Yup.string().nullable(),
  ceoEmail: Yup.string()
    .trim()
    .email("Email không đúng định dạng")
    .nullable(),
  charterCapital: Yup.string().trim().nullable(),
  employeeCount: Yup.string().trim().nullable(),
  establishedDate: Yup.date()
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue == null) return null;
      return value;
    }),
  productService: Yup.string().trim().nullable(),
  branchOffice: Yup.string().nullable(),
  position: Yup.string().trim().nullable(),
  joinAt: Yup.date()
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue == null) return null;
      return value;
    })
    .typeError("Ngày tham gia hội không hợp lệ")
    .required("Ngày tham gia hội không được để trống"),
});

const userIdSchema = Yup.object().shape({
  userId: Yup.number()
    .typeError("Vui lòng chọn người đại diện")
    .integer("Vui lòng chọn người đại diện")
    .positive("Vui lòng chọn người đại diện")
    .required("Vui lòng chọn người đại diện"),
});

const getPartnerSchema = (memberType) => {
  return memberType === "individual"
    ? individualPartnerSchema
    : companyPartnerSchema;
};

const getValidateSchema = (memberType) => {
  return memberType === "individual"
    ? individualPartnerSchema
    : companyPartnerSchema.concat(userIdSchema);
};

const companyFieldLabels = {
  userFullName: "Họ và tên",
  yearOfBirth: "Sinh nhật",
  userEmail: "Email đơn vị",
  hometown: "Nơi sinh",
  userPhone: "Số điện thoại đơn vị",
  companyZaloPhone: "Số điện thoại Zalo",
  companyName: "Tên đơn vị",
  businessField: "Ngành nghề kinh doanh",
  businessAddress: "Địa chỉ đơn vị",
  websiteOrHotline: "Website",
  hotline: "Hotline",
  taxCode: "Mã số thuế",
  companyNameEn: "Tên tiếng Anh",
  companyShortName: "Tên viết tắt",
};

const companyRepresentFieldLabels = {
  userFullName: "Họ và tên",
  userEmail: "Email",
  userPhone: "Số điện thoại liên hệ",
  userAddress: "Địa chỉ",
  placeOfBirth: "Nơi sinh",
  dateOfBirth: "Sinh nhật",
  idCard: "Số căn cước",
  userAvatar: "Ảnh đại diện",
  position: "Chức vụ tại đơn vị",
};

const MemberTypeSwitcher = ({ value, disabled, onChange }) => {
  const isCompany = value !== "individual";

  const current = isCompany
    ? {
        label: "Doanh nghiệp",
        nextValue: "individual",
        className: "bg-blue-600 text-white border-blue-600 hover:bg-blue-700",
      }
    : {
        label: "Cá nhân",
        nextValue: "company",
        className:
          "bg-yellow-400 text-yellow-900 border-yellow-400 hover:bg-yellow-500",
      };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(current.nextValue)}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-semibold shadow-sm transition-all duration-200 ${
        current.className
      } ${disabled ? "opacity-60 cursor-not-allowed" : "hover:shadow-md"}`}
      title={`Bấm để chuyển sang ${
        isCompany ? "hội viên cá nhân" : "hội viên doanh nghiệp"
      }`}
    >
      <span>{current.label}</span>
      {!disabled && <ArrowsRightLeftIcon className="w-4 h-4" />}
    </button>
  );
};

const PartnerModal = ({ companyId, onClose, onSubmit, representList }) => {
  const [formData, setFormData] = useState({
    memberType: "company",
    userFullName: "",
    yearOfBirth: "",
    userEmail: "",
    images: "",
    hometown: "",
    userPhone: "",
    companyZaloPhone: "",
    companyName: "",
    businessField: "",
    businessAddress: "",
    websiteOrHotline: "",
    hotline: "",
    userMember: [],
    nonEditable: true,
    taxCode: "",
    companyNameEn: "",
    companyShortName: "",
    ceoFullName: "",
    ceoDateOfBirth: "",
    ceoPhone: "",
    ceoEmail: "",
    charterCapital: "",
    employeeCount: "",
    establishedDate: "",
    productService: "",
    branchOffice: "",
    joinAt: "",
    position: "",
  });

  const [companyRepresent, setCompanyRepresent] = useState(null);
  const [userMemberSelected, setUserMemberSelected] = useState([]);
  const [userMember, setUserMember] = useState([]);
  const [userMemberData, setUserMemberData] = useState([]);

  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState("");
  const [hasNewImage, setHasNewImage] = useState(false);
  const currentUserId = localStorage.getItem("userId");
  const dispatch = useDispatch();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [isMakeRepresentOpen, setIsMakeRepresentOpen] = useState(false);
  const [companyUserBeDeleted, setCompanyUserBeDeleted] = useState({});
  const [companyUserMadeRepresent, setCompanyUserMadeRepresent] = useState({});
  const [lockUpdate, setLockUpdate] = useState(null);
  const [company, setCompany] = useState(null);

  const isIndividualMember = formData.memberType === "individual";

  useEffect(() => {
    if (companyId) getCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCompany = () => {
    ApiCustom.get(`company-show-by-id/${companyId}`)
      .then((res) => setCompany(res.data?.data))
      .catch((err) => console.log(err));
  };

  const getCompanyRePresent = () =>
    company?.users.find((e) => e.CompaniesHasUsers.isUserRepresentOfCompany);

  const handleCloseDeleteModal = () => setIsDeleteOpen(false);

  const handleDeleteCompanyUser = () => {
    setUserMember(
      userMember.filter((e) => e.value !== companyUserBeDeleted.value),
    );
    if (String(formData.userId) === String(companyUserBeDeleted.value.id)) {
      setFormData((prev) => ({ ...prev, userId: -1 }));
      setCompanyRepresent(null);
    }
    setUserMemberData((prev) => [
      ...userMemberData.filter(
        (e) => e?.id !== companyUserBeDeleted?.value?.id,
      ),
    ]);
    setIsDeleteOpen(false);
  };

  const handleOpenLogoModal = () => {
    dispatch(fetchMedias());
    setIsLogoModalOpen(true);
  };

  const handleCloseLogoModal = () => setIsLogoModalOpen(false);

  const handlePressLogoImage = (media) => {
    setIsLogoModalOpen(false);
    setImagePreview(media.url);
    setHasNewImage(true);
    setFormData((prev) => ({
      ...prev,
      userAvatar: media.url,
      logoId: media?.id,
    }));
  };

  const handleCloseMadeRepresentModal = () => setIsMakeRepresentOpen(false);

  const handleMakeRepresentCompanyUser = () => {
    setCompanyRepresent(companyUserMadeRepresent?.value);

    const tmpUserMemberData = userMemberData.map((e) => {
      if (
        e.id === companyUserMadeRepresent?.value?.id &&
        e.isUserRepresentOfCompany != null
      ) {
        e.isUserRepresentOfCompany = true;
      } else {
        e.isUserRepresentOfCompany = false;
      }
      return e;
    });
    setUserMemberData(tmpUserMemberData);

    setFormData((prev) => ({
      ...prev,
      userId: companyUserMadeRepresent?.value?.id,
    }));
    setIsMakeRepresentOpen(false);
  };

  useEffect(() => {
    if (!company) return;

    if (company?.id) {
      setCompanyRepresent(getCompanyRePresent() ?? null);

      setUserMember(
        company?.users?.map((e) => ({ label: e?.userFullName, value: e })),
      );

      setUserMemberData(
        company?.users?.map((e) => ({
          id: e?.id,
          userPosition: e?.CompaniesHasUsers?.userPosition,
          isUserRepresentOfCompany:
            e?.CompaniesHasUsers?.isUserRepresentOfCompany,
        })),
      );

      setFormData((prev) => ({
        ...prev,
        userId:
          company?.users?.find(
            (e) => e?.CompaniesHasUsers?.isUserRepresentOfCompany,
          )?.id ?? -1,
        nonEditable: company.nonEditable ?? true,
        position:
          company?.users?.find(
            (e) => e?.CompaniesHasUsers?.isUserRepresentOfCompany,
          )?.CompaniesHasUsers?.userPosition ||
          company?.position ||
          "",
      }));

      const lock = company.nonEditable ?? true;
      const companyRepresentId =
        company?.users?.find(
          (e) => e?.CompaniesHasUsers?.isUserRepresentOfCompany,
        )?.id ?? -1;

      if (
        String(currentUserId) === String(companyRepresentId) &&
        String(currentUserId) !== "1"
      ) {
        setLockUpdate(lock);
      } else {
        setLockUpdate(hasPermission("COMPANY_UPDATE") ? lock : true);
      }
    }

    const { userId, ...newCompanyData } = company;
    setFormData((prev) => ({
      ...prev,
      ...newCompanyData,
      memberType: company.memberType || "company",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const getLogoImageUrl = () => {
    if (hasNewImage) return imagePreview;
    if (company && company.userAvatar) return company.userAvatar;
    return "";
  };

  const handleUserSelectorChange = async (userSelected) => {
    setUserMemberSelected(userSelected);
  };

  const handleLockUpdate = async ({ useToast = true } = {}) => {
    try {
      const response = await apiCustom.put(
        `/company-lock-update`,
        { id: company.id, nonEditable: !lockUpdate },
        { headers: { "Content-Type": "application/json" } },
      );
      if (response.status === 200) {
        setLockUpdate(!lockUpdate);
        setFormData((prev) => ({ ...prev, nonEditable: !lockUpdate }));
        if (useToast) {
          toast.success(
            !lockUpdate
              ? "Đã khoá thông tin hội viên!"
              : "Đã mở khoá thông tin hội viên!",
          );
        }
      }
    } catch (error) {
      toast.error("Đã xảy ra lỗi!");
    }
  };

  const handleMemberTypeChange = (nextMemberType) => {
    if (lockUpdate) return;
    if (nextMemberType === formData.memberType) return;

    setFormData((prev) => ({
      ...prev,
      memberType: nextMemberType,
    }));

    setErrors({});
  };

  const validateField = async (name, value, memberType) => {
    try {
      const validateSchema = getValidateSchema(memberType);
      await Yup.reach(validateSchema, name).validate(value);
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    } catch (validationError) {
      if (validationError?.name === "ValidationError") {
        setErrors((prev) => ({ ...prev, [name]: validationError.message }));
      }
    }
  };

  const handleChange = async (e) => {
    const { name, value, files } = e.target;
    let newValue = value;

    if (name === "images" && files?.[0]) {
      const file = files[0];
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          [name]: "Hình ảnh phải có kích thước nhỏ hơn hoặc bằng 10Mb",
        }));
        return;
      }
      newValue = file;
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setHasNewImage(true);
      };
      reader.readAsDataURL(file);
    }

    if (name === "userId" && Number(newValue) > -1) {
      const idNum = parseInt(newValue, 10);

      const tmpUserMemberData = userMemberData.map((e) => {
        if (e.id === idNum && e.isUserRepresentOfCompany != null) {
          e.isUserRepresentOfCompany = true;
        } else {
          e.isUserRepresentOfCompany = false;
        }
        return e;
      });

      const exists = userMember.map((e) => e.value.id).includes(idNum);

      if (!exists) {
        const item = representList.find((e) => e.id === idNum);
        if (item) {
          setCompanyRepresent(item);
          setUserMember((prev) => [
            ...prev,
            { label: item.userFullName, value: item },
          ]);
          setUserMemberData((prev) => [
            ...tmpUserMemberData,
            { id: item.id, userPosition: "", isUserRepresentOfCompany: true },
          ]);
        }
      } else {
        setCompanyRepresent(
          userMember.find((e) => e?.value?.id === idNum)?.value,
        );
      }
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    const nextMemberType =
      name === "memberType" ? newValue : formData.memberType;

    await validateField(name, newValue, nextMemberType);
  };

  const handleExportApplicationDocx = async (cid) => {
    try {
      const response = await ApiCustom.get(
        `/company-export-application-docx/${cid}`,
        {
          responseType: "blob",
          headers: {
            "Content-Type":
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          },
        },
      );

      if (response.status !== 200) {
        toast.error("Không thể xuất file Word. Vui lòng thử lại!");
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "don_dang_ky_hoi_vien.docx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      window.URL.revokeObjectURL(url);
      toast.success("Đã xuất file đơn đăng ký!");
    } catch (error) {
      console.log(error);
      toast.error("Có lỗi xảy ra khi xuất đơn!");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const validateSchema = getValidateSchema(formData.memberType);
      await validateSchema.validate(formData, { abortEarly: false });

      const payload = { ...formData };
      payload.memberType = payload.memberType || "company";
      payload.companyName = (payload.companyName || "").toUpperCase();

      if (payload.memberType === "individual" && companyRepresent?.id) {
        payload.userId = companyRepresent.id;
        payload.userMemberData =
          userMemberData.length > 0
            ? userMemberData.map((item) => ({
                ...item,
                userPosition: payload.position || item.userPosition || "",
                isUserRepresentOfCompany:
                  String(item.id) === String(companyRepresent.id),
              }))
            : [
                {
                  id: companyRepresent.id,
                  userPosition: payload.position || "",
                  isUserRepresentOfCompany: true,
                },
              ];
      } else {
        payload.userMemberData = userMemberData;
      }

      if (company?.id) {
        const { userId, ...updateFormData } = payload;

        ApiCustom.put(`/company-update-by-id/${company.id}`, updateFormData)
          .then(async (res) => {
            if (res.status === 200) {
              await handleLockUpdate({ useToast: false });
            }
            toast.success("Cập nhật hội viên thành công");
            onSubmit && onSubmit();
            onClose && onClose();
          })
          .catch((error) => {
            if (error.response?.status === 422) {
              const msgs = error?.response?.data?.messages || {};
              if (!Object.keys(msgs).includes("representUser")) {
                setErrors(msgs);
              }
              toast.error(Object.values(msgs)[0] || "Lỗi cập nhật");
            }
          });
      } else {
        ApiCustom.post(`/company-create`, payload)
          .then((res) => {
            if (payload.memberType === "individual" && !payload.userId) {
              onSubmit && onSubmit();
              toast.success("Thêm hội viên thành công");
              onClose && onClose();
              return;
            }

            const representDataUpdate = {
              id: payload.userId,
              companyId: res?.data?.data?.id,
            };

            ApiCustom.put(`/user-update-company`, representDataUpdate)
              .then(() => {
                onSubmit && onSubmit();
                toast.success("Thêm hội viên thành công");
                onClose && onClose();
              })
              .catch((error) => {
                if (error.response?.status === 422) {
                  const msgs = error?.response?.data?.messages || {};
                  if (!Object.keys(msgs).includes("representUser")) {
                    setErrors(msgs);
                  } else {
                    toast.error(Object.values(msgs)[0]);
                  }
                }
              });
          })
          .catch((error) => {
            if (error.response?.status === 422) {
              const msgs = error?.response?.data?.messages || {};
              if (!Object.keys(msgs).includes("representUser")) {
                setErrors(msgs);
              } else {
                toast.error(Object.values(msgs)[0]);
              }
            }
          });
      }
    } catch (error) {
      if (error.inner) {
        const formErrors = {};
        error.inner.forEach((err) => {
          formErrors[err.path] = err.message;
        });
        setErrors(formErrors);
      } else {
        const msgs = error?.response?.data?.messages || {};
        if (!Object.keys(msgs).includes("representUser")) {
          setErrors(msgs);
        } else {
          toast.error(Object.values(msgs)[0]);
        }
      }
    }
  };

  const getUserPermissions = () => {
    const permissions = localStorage.getItem("permissions");
    return permissions
      ? JSON.parse(permissions).map((p) => p.permissionSlug)
      : [];
  };

  const hasPermission = (requiredPermission) => {
    if (String(currentUserId) === "1") return true;
    const userPermissions = getUserPermissions();
    return userPermissions.includes(requiredPermission);
  };

  /**
  const handleSendApprove = async (e) => {
    e.preventDefault();
    try {
      ApiCustom.put(
        `/users/${company.id}/send-approve`,
        { approvalStatus: "Gửi duyệt" },
        { headers: { "Content-Type": "application/json" } },
      )
        .then(() => {
          toast.success("Gửi duyệt thành công");
          onClose && onClose();
        })
        .catch((err) => console.log(err));
    } catch (error) {
      console.log(error);
      toast.error("Đã xảy ra lỗi! Vui lòng thử lại.");
    }
  };  */

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
      <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div>
          <div className="p-6 rounded-lg sticky top-0 bg-base-100 z-10">
            <div className="flex justify-between items-center ">
              <h2 className="text-2xl font-bold ">
                {company?.id
                  ? "Chỉnh sửa thông tin hội viên"
                  : "Thêm hội viên mới"}
              </h2>
              <button
                onClick={onClose}
                className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
              >
                X
              </button>
            </div>
          </div>

          <form className="space-y-8">
            <div className="px-6">
              <div
                className={`mb-6 rounded-xl border px-4 py-3 ${
                  isIndividualMember
                    ? "bg-yellow-50 border-yellow-200"
                    : "bg-blue-50 border-blue-200"
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3
                      className={`font-bold text-lg ${
                        isIndividualMember ? "text-yellow-900" : "text-blue-900"
                      }`}
                    >
                      {isIndividualMember
                        ? "Loại hội viên cá nhân"
                        : "Loại hội viên doanh nghiệp"}
                    </h3>

                    <p
                      className={`text-sm mt-0.5 ${
                        isIndividualMember ? "text-yellow-700" : "text-blue-700"
                      }`}
                    >
                      {isIndividualMember
                        ? "Hội viên cũng chính là người đại diện."
                        : "Quản lý thông tin đơn vị, người đại diện và thành viên trong đơn vị."}
                    </p>
                  </div>

                  <MemberTypeSwitcher
                    value={formData.memberType}
                    disabled={lockUpdate}
                    onChange={handleMemberTypeChange}
                  />
                </div>
              </div>

              <div
                className={`grid grid-cols-1 ${
                  isIndividualMember ? "" : "md:grid-cols-2"
                } gap-6`}
              >
                <div className="space-y-6">
                  <div
                    className={
                      isIndividualMember
                        ? "grid grid-cols-1 md:grid-cols-2 gap-4"
                        : "space-y-4"
                    }
                  >
                    {company?.id && (
                      <h3
                        className={`font-bold text-center text-lg py-0 my-0 ${
                          isIndividualMember ? "md:col-span-2" : ""
                        }`}
                      >
                        {isIndividualMember
                          ? "Thông tin hội viên"
                          : "Thông tin đơn vị"}
                      </h3>
                    )}

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {isIndividualMember
                          ? "Tên hội viên"
                          : companyFieldLabels.companyName}
                        <i className="text-red-600"> *</i>
                      </label>
                      <input
                        style={{ textTransform: "uppercase" }}
                        disabled={lockUpdate}
                        type="text"
                        name="companyName"
                        value={formData.companyName || ""}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md focus:ring-2 ${
                          errors.companyName
                            ? "border-red-500"
                            : "border-gray-300"
                        } focus:ring-blue-500 focus:border-transparent`}
                      />
                      {errors.companyName && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.companyName}
                        </p>
                      )}
                    </div>

                    {!isIndividualMember && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {["companyNameEn", "companyShortName"].map((key) => (
                          <div key={key}>
                            <label className="block text-sm font-medium mb-1">
                              {companyFieldLabels[key]}
                            </label>
                            <input
                              type="text"
                              disabled={lockUpdate}
                              name={key}
                              value={formData[key] || ""}
                              onChange={handleChange}
                              className={`w-full p-2 border rounded-md focus:ring-2 ${
                                errors[key]
                                  ? "border-red-500"
                                  : "border-gray-300"
                              } focus:ring-blue-500 focus:border-transparent`}
                            />
                            {errors[key] && (
                              <p className="mt-1 text-sm text-red-600">
                                {errors[key]}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {isIndividualMember ? (
                      <>
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Số điện thoại liên hệ
                            <i className="text-red-600"> *</i>
                          </label>
                          <input
                            type="text"
                            disabled={lockUpdate}
                            name="userPhone"
                            value={formData.userPhone || ""}
                            onChange={handleChange}
                            className={`w-full p-2 border rounded-md focus:ring-2 ${
                              errors.userPhone
                                ? "border-red-500"
                                : "border-gray-300"
                            } focus:ring-blue-500 focus:border-transparent`}
                          />
                          {errors.userPhone && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.userPhone}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Email hội viên
                          </label>
                          <input
                            type="text"
                            disabled={lockUpdate}
                            name="userEmail"
                            value={formData.userEmail || ""}
                            onChange={handleChange}
                            className={`w-full p-2 border rounded-md focus:ring-2 ${
                              errors.userEmail
                                ? "border-red-500"
                                : "border-gray-300"
                            } focus:ring-blue-500 focus:border-transparent`}
                          />
                          {errors.userEmail && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.userEmail}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Số điện thoại Zalo
                          </label>
                          <input
                            type="text"
                            disabled={lockUpdate}
                            name="companyZaloPhone"
                            value={formData.companyZaloPhone || ""}
                            onChange={handleChange}
                            className={`w-full p-2 border rounded-md focus:ring-2 ${
                              errors.companyZaloPhone
                                ? "border-red-500"
                                : "border-gray-300"
                            } focus:ring-blue-500 focus:border-transparent`}
                          />
                          {errors.companyZaloPhone && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.companyZaloPhone}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1">
                            {companyFieldLabels.businessField}
                            <i className="text-red-600"> *</i>
                          </label>
                          <input
                            type="text"
                            name="businessField"
                            disabled={lockUpdate}
                            value={formData.businessField || ""}
                            onChange={handleChange}
                            className={`w-full p-2 border rounded-md focus:ring-2 ${
                              errors.businessField
                                ? "border-red-500"
                                : "border-gray-300"
                            } focus:ring-blue-500 focus:border-transparent`}
                          />
                          {errors.businessField && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.businessField}
                            </p>
                          )}
                        </div>

                        <div className="form-control">
                          <label className="block text-sm font-medium mb-1">
                            Ngày tham gia hội{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <DatePicker
                            disabled={lockUpdate}
                            name="joinAt"
                            value={formData.joinAt || ""}
                            onChange={(name, value) =>
                              handleChange({ target: { name, value } })
                            }
                            error={errors.joinAt}
                          />
                          {errors.joinAt && (
                            <span className="text-red-500 text-sm">
                              {errors.joinAt}
                            </span>
                          )}
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium mb-1">
                            Địa chỉ hội viên
                            <i className="text-red-600"> *</i>
                          </label>
                          <input
                            type="text"
                            name="businessAddress"
                            disabled={lockUpdate}
                            value={formData.businessAddress || ""}
                            onChange={handleChange}
                            className={`w-full p-2 border rounded-md focus:ring-2 ${
                              errors.businessAddress
                                ? "border-red-500"
                                : "border-gray-300"
                            } focus:ring-blue-500 focus:border-transparent`}
                          />
                          {errors.businessAddress && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.businessAddress}
                            </p>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {[
                            "userPhone",
                            "companyZaloPhone",
                            "userEmail",
                            "websiteOrHotline",
                          ].map((key) => (
                            <div key={key}>
                              <label className="block text-sm font-medium mb-1">
                                {companyFieldLabels[key]}
                                {["userPhone", "userEmail"].includes(key) && (
                                  <i className="text-red-600"> *</i>
                                )}
                              </label>
                              <input
                                type="text"
                                disabled={lockUpdate}
                                name={key}
                                value={formData[key] || ""}
                                onChange={handleChange}
                                className={`w-full p-2 border rounded-md focus:ring-2 ${
                                  errors[key]
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } focus:ring-blue-500 focus:border-transparent`}
                              />
                              {errors[key] && (
                                <p className="mt-1 text-sm text-red-600">
                                  {errors[key]}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>

                        {company?.id && (
                          <>
                            <div>
                              <label className="block text-sm font-medium mb-1">
                                {companyFieldLabels.businessAddress}
                                <i className="text-red-600"> *</i>
                              </label>
                              <input
                                type="text"
                                name="businessAddress"
                                disabled={lockUpdate}
                                value={formData.businessAddress || ""}
                                onChange={handleChange}
                                className={`w-full p-2 border rounded-md focus:ring-2 ${
                                  errors.businessAddress
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } focus:ring-blue-500 focus:border-transparent`}
                              />
                              {errors.businessAddress && (
                                <p className="mt-1 text-sm text-red-600">
                                  {errors.businessAddress}
                                </p>
                              )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium mb-1">
                                  {companyFieldLabels.taxCode}
                                  <i className="text-red-600"> *</i>
                                </label>
                                <input
                                  type="text"
                                  name="taxCode"
                                  disabled={lockUpdate}
                                  value={
                                    formData.taxCode !== "null"
                                      ? formData.taxCode || ""
                                      : ""
                                  }
                                  onChange={handleChange}
                                  className={`w-full p-2 border rounded-md focus:ring-2 ${
                                    errors.taxCode
                                      ? "border-red-500"
                                      : "border-gray-300"
                                  } focus:ring-blue-500 focus:border-transparent`}
                                />
                                {errors.taxCode && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {errors.taxCode}
                                  </p>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium mb-1">
                                  {companyFieldLabels.businessField}
                                  <i className="text-red-600"> *</i>
                                </label>
                                <input
                                  type="text"
                                  name="businessField"
                                  disabled={lockUpdate}
                                  value={formData.businessField || ""}
                                  onChange={handleChange}
                                  className={`w-full p-2 border rounded-md focus:ring-2 ${
                                    errors.businessField
                                      ? "border-red-500"
                                      : "border-gray-300"
                                  } focus:ring-blue-500 focus:border-transparent`}
                                />
                                {errors.businessField && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {errors.businessField}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="form-control">
                                <label className="block text-sm font-medium mb-1">
                                  Ngày thành lập{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                  disabled={lockUpdate}
                                  name="establishedDate"
                                  value={formData.establishedDate || ""}
                                  onChange={(name, value) =>
                                    handleChange({ target: { name, value } })
                                  }
                                  error={errors.establishedDate}
                                />
                                {errors.establishedDate && (
                                  <span className="text-red-500 text-sm">
                                    {errors.establishedDate}
                                  </span>
                                )}
                              </div>

                              <div className="form-control">
                                <label className="block text-sm font-medium mb-1">
                                  Ngày tham gia hội{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                  disabled={lockUpdate}
                                  name="joinAt"
                                  value={formData.joinAt || ""}
                                  onChange={(name, value) =>
                                    handleChange({ target: { name, value } })
                                  }
                                  error={errors.joinAt}
                                />
                                {errors.joinAt && (
                                  <span className="text-red-500 text-sm">
                                    {errors.joinAt}
                                  </span>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {!company?.id && !isIndividualMember && (
                      <div className="form-control mt-3">
                        <div className="block text-sm font-medium mb-1">
                          Người đại diện<i className="text-red-600"> *</i>
                        </div>
                        <select
                          name="userId"
                          value={formData.userId}
                          onChange={handleChange}
                          className={`w-full p-2 border rounded-md min-h-10 ${
                            errors.userId ? "border-red-500" : "border-gray-300"
                          } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                        >
                          <option value={-1}>Chọn người đại diện</option>
                          {representList?.length > 0 &&
                            representList?.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item?.userFullName}
                              </option>
                            ))}
                        </select>
                        {errors.userId && (
                          <p className="text-red-500 text-sm">
                            {errors.userId}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {!company?.id && !isIndividualMember && (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          {companyFieldLabels.businessAddress}
                          <i className="text-red-600"> *</i>
                        </label>
                        <input
                          type="text"
                          name="businessAddress"
                          disabled={lockUpdate}
                          value={formData.businessAddress || ""}
                          onChange={handleChange}
                          className={`w-full p-2 border rounded-md focus:ring-2 ${
                            errors.businessAddress
                              ? "border-red-500"
                              : "border-gray-300"
                          } focus:ring-blue-500 focus:border-transparent`}
                        />
                        {errors.businessAddress && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.businessAddress}
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            {companyFieldLabels.taxCode}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="taxCode"
                            disabled={lockUpdate}
                            value={formData.taxCode || ""}
                            onChange={handleChange}
                            className={`w-full p-2 border rounded-md focus:ring-2 ${
                              errors.taxCode
                                ? "border-red-500"
                                : "border-gray-300"
                            } focus:ring-blue-500 focus:border-transparent`}
                          />
                          {errors.taxCode && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.taxCode}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1">
                            {companyFieldLabels.businessField}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="businessField"
                            disabled={lockUpdate}
                            value={formData.businessField || ""}
                            onChange={handleChange}
                            className={`w-full p-2 border rounded-md focus:ring-2 ${
                              errors.businessField
                                ? "border-red-500"
                                : "border-gray-300"
                            } focus:ring-blue-500 focus:border-transparent`}
                          />
                          {errors.businessField && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.businessField}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                          <label className="block text-sm font-medium mb-1">
                            Ngày thành lập{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <DatePicker
                            disabled={lockUpdate}
                            name="establishedDate"
                            value={formData.establishedDate || ""}
                            onChange={(name, value) =>
                              handleChange({ target: { name, value } })
                            }
                            error={errors.establishedDate}
                          />
                          {errors.establishedDate && (
                            <span className="text-red-500 text-sm">
                              {errors.establishedDate}
                            </span>
                          )}
                        </div>

                        <div className="form-control">
                          <label className="block text-sm font-medium mb-1">
                            Ngày tham gia hội{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <DatePicker
                            disabled={lockUpdate}
                            name="joinAt"
                            value={formData.joinAt || ""}
                            onChange={(name, value) =>
                              handleChange({ target: { name, value } })
                            }
                            error={errors.joinAt}
                          />
                          {errors.joinAt && (
                            <span className="text-red-500 text-sm">
                              {errors.joinAt}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {company?.id && !isIndividualMember && (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <h3 className="font-bold text-center text-lg py-0 my-0">
                        Thông tin người đại diện
                      </h3>

                      {companyRepresent != null ? (
                        <>
                          {["userFullName", "userPhone", "userEmail"].map(
                            (key) => (
                              <div key={key}>
                                <label className="block text-sm font-medium mb-1">
                                  {companyRepresentFieldLabels[key]}
                                </label>
                                <input
                                  type="text"
                                  disabled
                                  value={
                                    ["dateOfBirth"].includes(key)
                                      ? formatDate(companyRepresent[key])
                                      : companyRepresent[key]
                                  }
                                  onChange={() => {}}
                                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              </div>
                            ),
                          )}

                          <div>
                            <label className="block text-sm font-medium mb-1">
                              {companyRepresentFieldLabels.dateOfBirth}
                            </label>
                            <input
                              type="text"
                              disabled
                              value={formatDate(
                                companyRepresent["dateOfBirth"],
                              )}
                              onChange={() => {}}
                              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-1">
                              {companyRepresentFieldLabels.position}
                            </label>
                            <input
                              type="text"
                              disabled
                              value={
                                companyRepresent?.CompaniesHasUsers
                                  ?.userPosition || ""
                              }
                              onChange={() => {}}
                              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </>
                      ) : (
                        <div className="w-full py-36 flex justify-center">
                          <div className="w-full flex-col justify-center items-center">
                            <h4 className="text-gray-500 mb-5 text-center">
                              Chưa có người đại diện
                            </h4>
                            <div className="form-control mt-3 w-80 m-auto">
                              <select
                                name="userId"
                                disabled={lockUpdate}
                                value={formData.userId}
                                onChange={handleChange}
                                className={`p-2.5 border rounded-md ${
                                  errors.userId
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                              >
                                <option value={-1}>Chọn người đại diện</option>
                                {representList?.length > 0 &&
                                  representList?.map((item) => (
                                    <option key={item.id} value={item.id}>
                                      {item?.userFullName}
                                    </option>
                                  ))}
                              </select>
                              {errors.userId && (
                                <p className="text-red-500 text-sm">
                                  {errors.userId}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {!isIndividualMember && (
                <div className="space-y-8 border-t pt-6 mt-4">
                  <div>
                    <h3 className="font-bold text-lg mb-6 border-b pb-2">
                      Thông tin Tổng giám đốc/Giám đốc
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Họ và tên <span className="text-red-500">*</span>
                        </label>
                        <input
                          name="ceoFullName"
                          type="text"
                          disabled={lockUpdate}
                          value={formData.ceoFullName || ""}
                          onChange={handleChange}
                          className={`w-full p-2 border rounded-md ${
                            errors.ceoFullName
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {errors.ceoFullName && (
                          <p className="text-red-500 text-sm">
                            {errors.ceoFullName}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Sinh nhật <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                          disabled={lockUpdate}
                          name="ceoDateOfBirth"
                          value={formData.ceoDateOfBirth || ""}
                          onChange={(name, value) =>
                            handleChange({ target: { name, value } })
                          }
                          error={errors.ceoDateOfBirth}
                        />
                        {errors.ceoDateOfBirth && (
                          <span className="text-red-500 text-sm">
                            {errors.ceoDateOfBirth}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Số điện thoại liên hệ{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          name="ceoPhone"
                          type="text"
                          disabled={lockUpdate}
                          value={formData.ceoPhone || ""}
                          onChange={handleChange}
                          className={`w-full p-2 border rounded-md ${
                            errors.ceoPhone
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {errors.ceoPhone && (
                          <p className="text-red-500 text-sm">
                            {errors.ceoPhone}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          name="ceoEmail"
                          type="email"
                          disabled={lockUpdate}
                          value={formData.ceoEmail || ""}
                          onChange={handleChange}
                          className={`w-full p-2 border rounded-md ${
                            errors.ceoEmail
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {errors.ceoEmail && (
                          <p className="text-red-500 text-sm">
                            {errors.ceoEmail}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!isIndividualMember && (
                <div className="border-t pt-3 mt-6 mb-8">
                  <div className="space-y-4">
                    <div className="grid max-sm:grid-cols-1 grid-cols-2 gap-4">
                      <h2 className="text-xl font-semibold">
                        Danh sách thành viên trong đơn vị
                      </h2>
                      <div className="flex max-sm:justify-between">
                        <UserSelection
                          disable={lockUpdate}
                          options={representList
                            .filter(
                              (e) =>
                                !userMember
                                  .map((m) => m?.value?.id)
                                  .includes(e?.id),
                            )
                            .map((e) => ({ label: e.userFullName, value: e }))}
                          onChange={handleUserSelectorChange}
                          selectedUsers={userMemberSelected}
                        />
                        <div
                          onClick={
                            lockUpdate
                              ? null
                              : (ev) => {
                                  ev.preventDefault();
                                  setUserMemberData((prev) => [
                                    ...userMemberData,
                                    ...userMemberSelected.map((item) => ({
                                      id: item.value.id,
                                      userPosition: "",
                                      isUserRepresentOfCompany: false,
                                    })),
                                  ]);
                                  setUserMember((prev) => [
                                    ...prev,
                                    ...userMemberSelected,
                                  ]);
                                  setUserMemberSelected([]);
                                }
                          }
                          className={`ml-4 flex items-center h-11 justify-center px-4 py-0 ${
                            lockUpdate
                              ? "bg-gray-200 border-gray-300"
                              : "text-white bg-green-700 hover:bg-green-800"
                          } border border-transparent rounded-md shadow-sm text-sm font-medium cursor-pointer`}
                        >
                          <PlusIcon className="w-5 h-5" />
                          <span>Thêm</span>
                        </div>
                      </div>
                    </div>

                    {userMember.length > 0 ? (
                      <div className="grid gap-4 border p-4 rounded-xl shadow-sm bg-white">
                        {userMember.map((item) => (
                          <div
                            className="grid md:grid-cols-3 gap-4"
                            key={item?.value?.id}
                          >
                            <div>
                              <div className="mb-1">Họ và tên</div>
                              <div className="border rounded-lg px-4 py-2 w-full bg-gray-100">
                                {item.value.userFullName}
                              </div>
                            </div>

                            <div>
                              <div className="mb-1">Chức vụ tại đơn vị</div>
                              <input
                                type="text"
                                disabled={lockUpdate}
                                value={
                                  userMemberData.find(
                                    (e) => e?.id === item?.value?.id,
                                  )?.userPosition ?? ""
                                }
                                onChange={(event) => {
                                  event.preventDefault();

                                  const tmpUserMemberData = userMemberData.map(
                                    (e) => {
                                      if (
                                        e.id === item?.value?.id &&
                                        e.userPosition != null
                                      ) {
                                        e.userPosition = event.target.value;
                                      }
                                      return e;
                                    },
                                  );
                                  setUserMemberData(tmpUserMemberData);

                                  const tmpUserMember = userMember.map((e) => {
                                    if (
                                      e?.value?.id === item?.value?.id &&
                                      e?.value?.CompaniesHasUsers
                                        ?.userPosition != null
                                    ) {
                                      e.value.CompaniesHasUsers.userPosition =
                                        event.target.value;
                                    } else {
                                      e.value = {
                                        ...e.value,
                                        CompaniesHasUsers: {
                                          userPosition: event?.target?.value,
                                        },
                                      };
                                    }
                                    return e;
                                  });
                                  setUserMember(tmpUserMember);

                                  if (
                                    companyRepresent &&
                                    item?.value?.id === companyRepresent?.id
                                  ) {
                                    setCompanyRepresent((prev) => ({
                                      ...prev,
                                      CompaniesHasUsers: {
                                        ...prev?.CompaniesHasUsers,
                                        userPosition: event?.target?.value,
                                      },
                                    }));
                                  }
                                }}
                                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-6">
                              <div
                                onClick={
                                  lockUpdate
                                    ? null
                                    : (e) => {
                                        e.preventDefault();
                                        if (
                                          item?.value?.id !==
                                          (companyRepresent?.id || -1)
                                        ) {
                                          setCompanyUserMadeRepresent(item);
                                          setIsMakeRepresentOpen(true);
                                        }
                                      }
                                }
                                className={`w-44 select-none px-4 py-1.5 h-10 text-center text-base ${
                                  lockUpdate
                                    ? "bg-gray-200 border-gray-300 text-gray-500"
                                    : parseInt(formData?.userId, 10) !==
                                        item?.value?.id
                                      ? "bg-blue-700 hover:bg-blue-800 text-white font-medium"
                                      : "bg-green-700 text-white font-medium"
                                } border border-transparent rounded-md shadow-sm cursor-pointer`}
                              >
                                {parseInt(formData?.userId, 10) !==
                                item?.value?.id
                                  ? "Thành viên"
                                  : "Đại diện"}
                              </div>

                              <button
                                title="Xóa"
                                disabled={lockUpdate}
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setCompanyUserBeDeleted(item);
                                  setIsDeleteOpen(true);
                                }}
                                className="btn btn-outline h-10 btn-error btn-sm px-3 rounded"
                              >
                                <TrashIcon className="w-5 h-6" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center pb-6 pt-8 text-gray-500">
                        Chưa có thành viên
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-6 border-t pt-3 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {isIndividualMember ? "Logo hội viên" : "Logo đơn vị"}
                    </label>
                    <div className="mt-1 flex items-center space-x-4">
                      <div className="relative w-32 h-32 border-2 border-gray-300 border-dashed rounded-lg overflow-hidden">
                        {getLogoImageUrl() ? (
                          <img
                            src={getLogoImageUrl()}
                            alt="Preview"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <PhotoIcon className="w-10 h-10 text-gray-300" />
                          </div>
                        )}

                        {!lockUpdate && (
                          <PencilSquareIcon
                            className="h-4 w-4 absolute bottom-1 right-1 cursor-pointer rounded-md"
                            onClick={() =>
                              lockUpdate ? null : handleOpenLogoModal()
                            }
                          />
                        )}
                      </div>
                    </div>
                    {errors["images"] && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors["images"]}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`rounded-lg flex sticky bottom-0 bg-base-100 z-10 p-6 ${
                company ? "justify-between" : "justify-end"
              }`}
            >
              <div>
                <div className="space-x-4 pt-4 flex">
                  {company?.id &&
                    hasPermission("COMPANY_UPDATE") &&
                    isApproved && (
                      <button
                        type="button"
                        onClick={handleLockUpdate}
                        className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium flex items-center space-x-2 btn-warning hover:bg-yellow-500"
                      >
                        {lockUpdate ? (
                          <>
                            <LockClosedIcon className="h-5 w-5" />
                            <span>Đã khoá thông tin</span>
                          </>
                        ) : (
                          <>
                            <LockOpenIcon className="h-5 w-5" />
                            <span>Khoá thông tin</span>
                          </>
                        )}
                      </button>
                    )}

                  {company?.id &&
                    company?.users?.some(
                      (u) =>
                        u?.CompaniesHasUsers?.isUserRepresentOfCompany &&
                        String(u.id) === String(currentUserId),
                    ) && (
                      <button
                        type="button"
                        onClick={() => handleExportApplicationDocx(company?.id)}
                        className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white ml-2"
                        title="Xuất đơn đăng ký hội viên"
                      >
                        <NewspaperIcon className="h-5 w-5" />
                        <span>Xuất đơn đăng ký</span>
                      </button>
                    )}
                </div>
              </div>

              <div className="flex items-end justify-end space-x-4 border-t pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  {lockUpdate ? "Đóng" : "Huỷ"}
                </button>

                {!lockUpdate && (
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {company?.id ? "Cập nhật" : "Thêm mới"}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      {isLogoModalOpen && (
        <AvatarImageModal
          imageableId={currentUserId}
          imageableType={"companie"}
          imageType={"logo"}
          avatarType={true}
          onClose={handleCloseLogoModal}
          onPress={handlePressLogoImage}
        />
      )}

      <ConfirmDeleteCompanyUserModal
        isOpen={isDeleteOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteCompanyUser}
      />

      <ConfirmMakeCompanyUserRepresentModal
        isOpen={isMakeRepresentOpen}
        onClose={handleCloseMadeRepresentModal}
        onConfirm={handleMakeRepresentCompanyUser}
      />
    </div>
  );
};

export default PartnerModal;